import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { allowedLanguages } from '@core-config';
import { TranslateService } from '@ngx-translate/core';
import { Language, LocalStorageKey } from '@shared-enums';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  //#region Service properties

  #defaultLanguage = Language.HR;
  #currentLanguage = Language.HR;

  #translateService = inject(TranslateService);
  #platformId = inject(PLATFORM_ID);

  #currentLanguage$ = new BehaviorSubject<Language>(Language.HR);
  language$ = this.#currentLanguage$.asObservable();

  //#endregion

  //#region Init

  init(): void {
    this.#loadCurrentLanguageFromStorage();
    this.#setCurrentLanguageAsDefault();
    this.#useCurrentLanguage();
  }

  //#endregion

  //#region Utilities

  #loadCurrentLanguageFromStorage(): void {
    this.#currentLanguage = this.#defaultLanguage;
    if (isPlatformBrowser(this.#platformId)) {
      const previousLanguage = localStorage.getItem(LocalStorageKey.Language);
      if (previousLanguage) {
        this.#currentLanguage = this.#validateLanguage(
          previousLanguage as unknown as Language,
        );
      }
    }
  }

  #validateLanguage(language: Language): Language {
    return allowedLanguages.includes(language) ? language : this.#defaultLanguage;
  }

  #setCurrentLanguageAsDefault(): void {
    this.#translateService.setDefaultLang(this.#currentLanguage);
  }

  #saveCurrentLanguage(): void {
    if (isPlatformBrowser(this.#platformId)) {
      localStorage.setItem(LocalStorageKey.Language, this.#currentLanguage);
    }
  }

  #useCurrentLanguage(): void {
    this.#translateService.use(this.#currentLanguage);
    this.#currentLanguage$.next(this.#currentLanguage);
  }

  //#endregion

  //#region Public methods

  setCurrentLanguage(language: Language): void {
    this.#currentLanguage = this.#validateLanguage(language);
    this.#useCurrentLanguage();
    this.#saveCurrentLanguage();
    this.#currentLanguage$.next(language);
  }

  getCurrentLanguage(): Language {
    return this.#currentLanguage ?? this.#defaultLanguage;
  }

  //#endregion
}
